import React from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  AlertTriangle,
  Clock
} from 'lucide-react';

const MarketOverview = ({ data }) => {
  const {
    totalRecommendations,
    buySignals,
    sellSignals,
    holdSignals,
    averageRiskScore,
    sectors,
    lastUpdated
  } = data;

  const getRiskColor = (score) => {
    if (score <= 3) return 'text-success-600';
    if (score <= 6) return 'text-warning-600';
    return 'text-danger-600';
  };

  const getRiskLabel = (score) => {
    if (score <= 3) return 'Low Risk';
    if (score <= 6) return 'Medium Risk';
    return 'High Risk';
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Market Overview</h2>
        {lastUpdated && (
          <div className="flex items-center space-x-1 text-sm text-gray-500">
            <Clock className="h-4 w-4" />
            <span>
              {new Date(lastUpdated).toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        {/* Total Recommendations */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Activity className="h-6 w-6 text-primary-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {totalRecommendations}
          </div>
          <div className="text-sm text-gray-600">
            Total Recommendations
          </div>
        </div>

        {/* Buy Signals */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <TrendingUp className="h-6 w-6 text-success-600" />
          </div>
          <div className="text-2xl font-bold text-success-600">
            {buySignals}
          </div>
          <div className="text-sm text-gray-600">
            Buy Signals
          </div>
        </div>

        {/* Sell Signals */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <TrendingDown className="h-6 w-6 text-danger-600" />
          </div>
          <div className="text-2xl font-bold text-danger-600">
            {sellSignals}
          </div>
          <div className="text-sm text-gray-600">
            Sell Signals
          </div>
        </div>

        {/* Average Risk */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <AlertTriangle className="h-6 w-6 text-warning-600" />
          </div>
          <div className={`text-2xl font-bold ${getRiskColor(averageRiskScore)}`}>
            {averageRiskScore.toFixed(1)}
          </div>
          <div className="text-sm text-gray-600">
            {getRiskLabel(averageRiskScore)}
          </div>
        </div>
      </div>

      {/* Signal Distribution */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Signal Distribution</h3>
        <div className="flex rounded-lg overflow-hidden h-3 bg-gray-200">
          {buySignals > 0 && (
            <div 
              className="bg-success-500"
              style={{ width: `${(buySignals / totalRecommendations) * 100}%` }}
              title={`${buySignals} Buy Signals`}
            />
          )}
          {holdSignals > 0 && (
            <div 
              className="bg-gray-400"
              style={{ width: `${(holdSignals / totalRecommendations) * 100}%` }}
              title={`${holdSignals} Hold Signals`}
            />
          )}
          {sellSignals > 0 && (
            <div 
              className="bg-danger-500"
              style={{ width: `${(sellSignals / totalRecommendations) * 100}%` }}
              title={`${sellSignals} Sell Signals`}
            />
          )}
        </div>
        <div className="flex justify-between text-xs text-gray-600 mt-2">
          <span>Buy ({buySignals})</span>
          <span>Hold ({holdSignals})</span>
          <span>Sell ({sellSignals})</span>
        </div>
      </div>

      {/* Sectors */}
      {sectors && sectors.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Active Sectors</h3>
          <div className="flex flex-wrap gap-2">
            {sectors.map((sector, index) => (
              <span 
                key={index}
                className="badge-neutral"
              >
                {sector}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketOverview;